
import { saveAs } from 'file-saver';
import XLSX from 'xlsx';
import PDFDocument from 'pdfkit/js/pdfkit.standalone';
import blobStream from 'blob-stream';

const COLUMN_OPTIONS = [
  { label: 'Identifier Type', value: 'Identifier Type', important: true, optional: false },
  { label: 'Identifier', value: 'Identifier Value', important: true, optional: false },
  { label: 'Item Name', value: 'Item Name', important: true, optional: false },
  { label: 'Brand', value: 'Brand', important: true, optional: false },
  { label: 'Unit Cost', value: 'Unit Cost', important: true, optional: false },
  { label: 'Order Quantity', value: 'Order Quantity', important: true, optional: false },
  { label: 'Invoice Quantity', value: 'Invoice Quantity', important: false, optional: true },
  { label: 'Case Quantity', value: 'Case Quantity', important: false, optional: true },
  { label: 'Notes', value: 'Notes', important: false, optional: true }
];

const EDIT_COLUMN_OPTIONS = [
  { label: 'identifier_type', value: 'identifier_type', important: true, optional: false }, // making identifier type required
  { label: 'identifier_value', value: 'identifier_value', important: true, optional: false },
  { label: 'unit_cost', value: 'unit_cost', important: true, optional: false },
  { label: 'order_quantity', value: 'order_quantity', important: false, optional: true },
  { label: 'invoice_quantity', value: 'invoice_quantity', important: true, optional: false },
  { label: 'case_quantity', value: 'case_quantity', important: false, optional: true },
  { label: 'notes', value: 'notes', important: false, optional: true }
];

const UPLOAD_MAPPING_OPTIONS = [
  { label: 'Identifier', value: 'Identifier Value', important: true, optional: false },
  { label: 'Sku', value: 'Sku', important: true, optional: false }
];

const poTemplateDownloadableFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/po-template-file.xlsx';
const mappingTemplateDownloadableFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/mapping-template-file.xlsx';
const bulkAddInventorySampleFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/bulk-add-inventory-file.xlsx';

const generateExcel = ({ pickList, orderName }) => {
  const headers = [['Location', 'Title', 'UPC', 'Brand', 'Quantity', 'Image Url']];

  const data = pickList.map(item => [
    item.location,
    item.item_name,
    item.upc,
    item.brand,
    item.picked_quantity,
    item?.image_url || '',
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...data]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'PickList');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  saveAs(blob, `${orderName}.xlsx`);
};

const fetchImageAsDataUri = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error fetching image:', error);
    return null;
  }
};

// helper to check if url is valid and looks like an image
const isValidImageUrl = (url) => {
  try {
    const parsed = new URL(url);
    return (
      ["http:", "https:"].includes(parsed.protocol) &&
      /\.(jpeg|jpg|png|gif|webp|bmp|svg)$/i.test(parsed.pathname)
    );
  } catch {
    return false;
  }
};


const truncateText = (text, maxLength) => {
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const generatePDF = async ({ pickList, orderName }) => {
  const doc = new PDFDocument({ size: 'A4', margin: 50 });
  const stream = doc.pipe(blobStream());

  // **Title**
  doc.fontSize(20).text(`Pick List - ${orderName}`, { align: 'center', underline: true }).moveDown(1.5);

  // **Headers**
  const startX = 20;
  const colWidths = [80, 60, 200, 100, 80, 60]; // Column Widths
  const rowHeight = 80; // Height of each row
  let currentY = doc.y;

  doc.fontSize(12).fillColor('black')
    .text('Location', startX, currentY, { width: colWidths[0], bold: true })
    .text('Picture', startX + colWidths[0], currentY, { width: colWidths[1], bold: true })
    .text('Title', startX + colWidths[0] + colWidths[1], currentY, { width: colWidths[2], bold: true })
    .text('UPC', startX + colWidths[0] + colWidths[1] + colWidths[2], currentY, { width: colWidths[3], bold: true })
    .text('Brand', startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], currentY, { width: colWidths[4], bold: true })
    .text('Qty', startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4], currentY, { width: colWidths[5], bold: true });

  doc.moveTo(startX, doc.y + 15).lineTo(startX + colWidths.reduce((a, b) => a + b, 0) - 30, doc.y + 15).stroke();
  currentY += 39; // Move Y position

  doc.fontSize(12).fillColor('black');

  for (const item of pickList) {
    if (currentY + rowHeight > 750) { // Check if new page is needed
      doc.addPage();
      currentY = 50;
    }

    doc.fontSize(10).text(item.location, startX, currentY, { width: colWidths[0] });

    // **Image**
    if (item.image_url && isValidImageUrl(item.image_url)) {
      try {
        const imageDataUri = await fetchImageAsDataUri(item.image_url);
    
        if (imageDataUri) {
          doc.image(
            imageDataUri,
            startX + colWidths[0] + 5,
            currentY,
            { width: 30, height: 30 }
          );
        }
      } catch (err) {
        console.warn("Skipping broken image:", item.image_url, err.message);
      }
    } else {
      console.warn("Invalid image URL, skipping:", item.image_url);
    }

    // **Product Details**
    doc.text(item?.item_name, startX + colWidths[0] + colWidths[1], currentY, { width: colWidths[2], continued: true }).text('\n');

    doc.text(item.upc, startX + colWidths[0] + colWidths[1] + colWidths[2] + 10, currentY, { width: colWidths[3] });
    doc.text(item?.brand, startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], currentY, { width: colWidths[4], continued: true }).text('\n');
    doc.text(item.picked_quantity.toString(), startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + 5, currentY, { width: colWidths[5] });

    currentY += rowHeight; // Move down for next row
  }

  // **Finalize PDF**
  doc.end();

  // **Handle PDF Download**
  stream.on('finish', () => {
    const url = stream.toBlobURL('application/pdf');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${orderName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });
};



export {
  COLUMN_OPTIONS,
  EDIT_COLUMN_OPTIONS,
  UPLOAD_MAPPING_OPTIONS,
  poTemplateDownloadableFile,
  mappingTemplateDownloadableFile,
  generateExcel,
  generatePDF,
  bulkAddInventorySampleFile
};
